<template>
    <div
        class="ask-pendo-artifact"
        :class="{ 'ask-pendo-artifact--fills-container': fillsContainer }">
        <ask-pendo-artifact-header
            :menu-items="menuItems"
            :is-inline="isInline"
            :is-preview-expandable="isPreviewExpandable"
            @toggle="$emit('toggle')">
            <template #left>
                <slot name="title">
                    <div class="ask-pendo-artifact__title">
                        <pendo-icon
                            v-if="artifactIcon"
                            :type="artifactIcon"
                            size="16" />
                        <span v-if="artifactName">{{ artifactName }}</span>
                    </div>
                </slot>
            </template>
            <template #right>
                <component
                    :is="headerItemsComponent"
                    v-if="headerItemsComponent"
                    :key="artifactId"
                    :data="artifactData"
                    :apps="apps"
                    @app-changed="handleAppChange" />
            </template>
        </ask-pendo-artifact-header>
        <div
            v-if="artifactConfig"
            class="ask-pendo-artifact__content"
            :class="{ 'ask-pendo-artifact__content--fullWidth': isFullWidth }">
            <component
                :is="artifactComponent"
                :key="artifactId"
                :artifact-id="artifactId"
                :data="artifactData"
                :is-inline="isInline"
                :is-streaming="isStreaming"
                :name="artifactName"
                :filters="artifactFilters" />
        </div>
        <div
            v-else
            class="ask-pendo-artifact__error">
            <h2>
                {{ $t('askPendo.artifact.Unable to display this artifact') }}
            </h2>
            <img src="@img/errors/data-not-found.svg">
        </div>
    </div>
</template>

<script>
import { PendoIcon } from '@pendo/components';
import CustomHighchartsArtifact from '@/ask-pendo/artifacts/custom-highcharts/custom-highcharts-artifact.vue';
import TableArtifact from '@/ask-pendo/artifacts/table-artifact/table-artifact.vue';
import TodoListArtifact from '@/ask-pendo/artifacts/todo-list-artifact/todo-list-artifact.vue';
import MarkdownTableArtifact from '@/ask-pendo/artifacts/markdown-table-artifact/markdown-table-artifact.vue';
import GuideStepPreviewArtifact from '@/ask-pendo/artifacts/guide-artifacts/guide-step-preview-artifact.vue';
import DocumentArtifact from '@/ask-pendo/artifacts/document-artifact/document-artifact.vue';
import SessionFilesArtifact from '@/ask-pendo/artifacts/session-files-artifact/session-files-artifact.vue';
import AskPendoArtifactHeader from '@/ask-pendo/components/ask-pendo-artifact-header.vue';
import AskPendoToolbarMenu from '@/ask-pendo/components/ask-pendo-toolbar-menu.vue';
import { getArtifactIcon, getArtifactTitle } from '@/ask-pendo/artifacts/artifact.utils';
import { mapGetters, mapActions, mapState } from 'vuex';
import GuideStepPreviewHeaderMenuItems from '@/ask-pendo/artifacts/guide-artifacts/guide-step-preview-header-menu-items.vue';
import { convertArtifactToGuide } from '@/ask-pendo/utils/ask-pendo-artifact.utils';
import { TEMPLATE_TYPES } from '@/guides/helpers/templates/ai/index';
import { isMobileApp } from '@/utils/apps';

const ARTIFACTS = {
    custom_highcharts: {
        component: CustomHighchartsArtifact,
        isFullWidth: false,
    },
    table: {
        component: TableArtifact,
        isFullWidth: true,
    },
    markdown_table: {
        component: MarkdownTableArtifact,
        isFullWidth: true,
    },
    guide: {
        component: GuideStepPreviewArtifact,
        headerItemsComponent: GuideStepPreviewHeaderMenuItems,
        isPreviewExpandable: false,
    },
    document: {
        component: DocumentArtifact,
        isFullWidth: true,
    },
    todo_list: {
        component: TodoListArtifact,
        isFullWidth: true,
    },
    session_files: {
        component: SessionFilesArtifact,
        isFullWidth: true,
        fillsContainer: true,
    },
};

export default {
    name: 'AskPendoArtifact',
    components: {
        AskPendoToolbarMenu,
        AskPendoArtifactHeader,
        CustomHighchartsArtifact,
        DocumentArtifact,
        GuideStepPreviewArtifact,
        GuideStepPreviewHeaderMenuItems,
        MarkdownTableArtifact,
        PendoIcon,
        SessionFilesArtifact,
        TableArtifact,
        TodoListArtifact,
    },
    props: {
        artifact: {
            type: Object,
            default: null,
        },
        isInline: {
            type: Boolean,
            default: false,
        },
        isStreaming: {
            type: Boolean,
            default: false,
        },
    },
    emits: ['toggle'],
    data () {
        return {
            artifactData: null,
            appId: -323232,
        };
    },
    computed: {
        ...mapState({
            segments: (state) => state.segments.map,
        }),
        ...mapGetters({
            getArtifactMenuItems: 'askPendo/getArtifactMenuItems',
            maxPriority: 'guides/maxPriority',
            isTrainingSubscription: 'subscription/isTrainingSubscription',
            getDefaultThemeForAppId: 'themes/getDefaultThemeForAppId',
            getAppById: 'apps/getAppById',
            apps: 'apps/list',
        }),
        artifactComponent () {
            return this.artifactConfig?.component;
        },
        artifactConfig () {
            return ARTIFACTS[this.artifact?.type];
        },
        artifactIcon () {
            return this.artifact ? getArtifactIcon(this.artifact) : '';
        },
        artifactId () {
            return this.artifact?.artifact_id;
        },
        artifactName () {
            return getArtifactTitle(this.artifact);
        },
        fillsContainer () {
            return !!this.artifactConfig?.fillsContainer;
        },
        isFullWidth () {
            return !!this.artifactConfig?.isFullWidth;
        },
        isPreviewExpandable () {
            return this.artifactConfig?.isPreviewExpandable ?? true;
        },
        menuItems () {
            return this.artifactId ? this.getArtifactMenuItems(this.artifactId) : [];
        },
        headerItemsComponent () {
            return this.artifactId ? ARTIFACTS[this.artifact?.type]?.headerItemsComponent : null;
        },
        artifactFilters () {
            return this.artifact?.filters;
        },
        app () {
            return this.getAppById(this.appId);
        },
        defaultTheme () {
            return this.getDefaultThemeForAppId(this.appId);
        },
    },
    watch: {
        artifact: {
            async handler (newVal) {
                switch (newVal?.type) {
                    case 'guide':
                        await this.loadThemes();
                        await this.loadSegments();
                        this.artifactData = await convertArtifactToGuide(newVal, this.maxPriority, this.isTrainingSubscription, this.defaultTheme, this.app, this.segments);
                        break;
                    default:
                        this.artifactData = newVal?.data;
                }
            },
            immediate: true,
        },
    },
    methods: {
        ...mapActions({
            loadThemes: 'themes/loadAll',
            loadSegments: 'segments/loadAll',
        }),
        async handleAppChange ({ appId, guide }) {
            this.appId = appId;
            if (!guide) return;

            const platform = this.app?.platform || 'web';
            const isMobile = isMobileApp(platform);
            const artifactLayoutType = this.artifact?.data?.layoutType;
            let targetLayoutType = artifactLayoutType;

            if (isMobile) {
                targetLayoutType = TEMPLATE_TYPES.MOBILE_ANNOUNCEMENT;
            } else if (artifactLayoutType === TEMPLATE_TYPES.MOBILE_ANNOUNCEMENT) {
                targetLayoutType = TEMPLATE_TYPES.ANNOUNCEMENT;
            }

            const updatedArtifact = {
                ...this.artifact,
                data: {
                    ...this.artifact.data,
                    layoutType: targetLayoutType,
                },
            };

            const newGuide = await convertArtifactToGuide(
                updatedArtifact,
                this.maxPriority,
                this.isTrainingSubscription,
                this.defaultTheme,
                this.app,
                this.segments,
            );

            Object.assign(guide, newGuide);
            // Remove mobile-specific properties for web apps
            if ( !isMobile && guide.minSdkVersion) {
                delete guide.minSdkVersion;
            }
        },
    },

};
</script>

<style scoped lang="less">
.ask-pendo-artifact {
    display: flex;
    flex-direction: column;
    min-height: 0;
    overflow-y: hidden;
    border: 1px solid var(--palette-gray-40);
    border-radius: 8px;

    &--fills-container {
        flex: 1;
    }

    &__custom_menu_items {
        margin-right: 8px;
    }

    &__title {
        display: flex;
        align-items: center;
        gap: 8px;

        padding: 4px 0;
        color: var(--palette-gray-100);
        font-weight: 600;
    }

    &__content {
        flex: 1 1 auto;
        height: 100%;
        display: flex;
        flex-direction: column;
        justify-content: center;
        align-items: stretch;
        overflow: hidden;
        background-color: var(--palette-gray-0);
    }

    &__content--fullWidth {
        max-width: unset;
    }

    &__error {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 32px;
        align-items: center;
        justify-content: center;

        h2 {
            font-size: 24px;
            font-weight: 600;
            line-height: 130%;
        }
    }
}

:deep(.pendo-card__header--left) {
    width: 65%;
}
:deep(.pendo-card__title) {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    line-height: 150%;
}
</style>
